import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCcw, Activity, CheckSquare, History, Clock,
  Calendar, CheckCircle, AlertCircle, XCircle,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../../core/services/localApiAdapter';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../../products/saudi/components/QawaemErpListPageShell';
import { QawaemErpViewModal } from '../../../products/saudi/components/QawaemErpCrudModal';
import QawaemClickableStatusBadge from '../../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../../products/saudi/components/QawaemTableHeaderActionsCell';
import { partitionStatusFromColumns } from '../../../shared/utils/erpListColumnOrder';
import { enrichStatusOptions } from '../../../shared/utils/crmPageUtils';
import useModuleConfigTheme from '../../../shared/hooks/useModuleConfigTheme';
import { exportToCSV } from '../../../shared/utils/importExportUtils';
import { useSaudiListDateRange } from '../../../products/saudi/hooks/useSaudiListDateRange';
import { useCrmConfig } from '../useCrmConfig';
import { resolveCrmListViewMode } from '../crmConfigDefaults';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from '../crmListPageUtils';
import { CRM_INTERACTIONS_REFRESH_EVENT, CRM_TIMELINE_REFRESH_EVENT, dispatchCrmApiRefresh } from '../crmRefreshEvents';
import { useCrmApiRefreshListener } from '../crmPickerOptions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../../products/saudi/components/QawaemTableStickyActions';
import {
  formatCrmDate,
  formatCrmMoney,
  isSaudiWeekend,
} from '../utils/crmSaudiFormat';

function normalizeTimeline(row) {
  return {
    id: row.id,
    ref: row.activity_ref || `ACT-${row.id}`,
    customer: row.customer_name || '—',
    type: row.activity_type || 'task',
    title: row.activity_title || '—',
    date: row.activity_date,
    duration: row.duration ?? 0,
    outcome: row.outcome || 'neutral',
    priority: row.priority || 'medium',
    status: row.status || 'completed',
    dealAmount: row.deal_amount ?? row.deal_value ?? null,
    notes: row.notes || '',
    assignedTo: row.assigned_to || '—',
  };
}

/**
 * Shared CRM list for timeline API (/crm/timeline).
 * Used by ActivitiesPage, TasksPage, HistoryPage, NotesPage.
 */
export default function CrmTimelineListPage({
  pageKey,
  typeFilter = null,
  statusFilter: statusFilterProp = null,
  notesOnly = false,
  showDealAmount = false,
  tasksOnly = false,
  historyOnly = false,
}) {
  const { t, i18n } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const p = (key, opts) => t(`modules.crm.pages.${pageKey}.${key}`, opts);
  const ps = (key, opts) => t(`modules.crm.pages.shared.${key}`, opts);
  const exportRef = useRef(null);

  const PageIcon = tasksOnly ? CheckSquare : historyOnly ? History : Activity;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    setViewMode(resolveCrmListViewMode(crmConfig.defaultView));
  }, [crmConfig.defaultView]);
  const [exportOpen, setExportOpen] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const tableColumnDefs = useMemo(() => {
    const cols = [
      { key: 'ref', labelKey: 'col_ref' },
      { key: 'title', labelKey: notesOnly ? 'col_note' : 'col_title' },
      { key: 'customer', labelKey: 'col_customer' },
    ];
    if (!notesOnly) cols.push({ key: 'type', labelKey: 'col_type' });
    cols.push({ key: 'date', labelKey: tasksOnly ? 'col_due_date' : 'col_date' });
    if (!notesOnly) cols.push({ key: 'priority', labelKey: 'col_priority' });
    cols.push({ key: 'status', labelKey: 'col_status', type: 'status' });
    if (showDealAmount) cols.push({ key: 'dealAmount', labelKey: 'col_deal_amount' });
    if (notesOnly) cols.push({ key: 'assignedTo', labelKey: 'col_author' });
    return cols;
  }, [notesOnly, showDealAmount, tasksOnly]);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(tableColumnDefs.map((c) => [c.key, true]))
  );

  useEffect(() => {
    setColumnVisibility(Object.fromEntries(tableColumnDefs.map((c) => [c.key, true])));
  }, [tableColumnDefs]);

  const typeLabel = (type) => ({
    call: ps('type_call'),
    email: ps('type_email'),
    meeting: ps('type_meeting'),
    whatsapp: ps('type_whatsapp'),
    task: ps('type_task'),
    note: ps('type_note'),
  }[type] || type);

  const priorityLabel = (pr) => ({
    high: ps('priority_high'),
    medium: ps('priority_medium'),
    low: ps('priority_low'),
  }[pr] || pr);

  const statusLabel = (s) => ({
    completed: ps('status_completed'),
    scheduled: ps('status_scheduled'),
    pending: ps('status_pending'),
    cancelled: ps('status_cancelled'),
  }[s] || s);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.activity_type = typeFilter;
      if (statusFilterProp) params.status = statusFilterProp;
      const qs = new URLSearchParams(params).toString();
      const response = await localApiAdapter.get(`/crm/timeline${qs ? `?${qs}` : ''}`);
      let data = response?.data || response || [];
      if (!Array.isArray(data)) data = [];
      setRecords(data.map(normalizeTimeline));
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilterProp]);

  useEffect(() => { void loadRecords(); }, [loadRecords]);
  useCrmApiRefreshListener([CRM_TIMELINE_REFRESH_EVENT, CRM_INTERACTIONS_REFRESH_EVENT], loadRecords);

  const baseFiltered = useMemo(() => {
    let list = filterCrmRecordsByDateRange(records, dateRange, ['activity_date', 'date']);
    if (tasksOnly) list = list.filter((r) => r.type === 'task' || ['scheduled', 'pending'].includes(r.status));
    if (historyOnly) list = list.filter((r) => r.status === 'completed');
    if (notesOnly) list = list.filter((r) => r.notes?.trim() || r.type === 'note');
    if (typeFilter) list = list.filter((r) => r.type === typeFilter);
    if (statusFilterProp) list = list.filter((r) => r.status === statusFilterProp);
    return list;
  }, [records, tasksOnly, historyOnly, notesOnly, typeFilter, statusFilterProp, dateRange]);

  const searchFiltered = useMemo(() => {
    if (viewMode !== 'cards' || !searchTerm) return baseFiltered;
    const q = searchTerm.toLowerCase();
    return baseFiltered.filter(
      (r) =>
        r.title?.toLowerCase().includes(q) ||
        r.customer?.toLowerCase().includes(q) ||
        r.ref?.toLowerCase().includes(q) ||
        r.notes?.toLowerCase().includes(q)
    );
  }, [baseFiltered, searchTerm, viewMode]);

  const stats = useMemo(() => ({
    total: searchFiltered.length,
    scheduled: searchFiltered.filter((r) => r.status === 'scheduled').length,
    pending: searchFiltered.filter((r) => r.status === 'pending').length,
    completed: searchFiltered.filter((r) => r.status === 'completed').length,
    overdue: searchFiltered.filter((r) => {
      if (!['scheduled', 'pending'].includes(r.status)) return false;
      return new Date(r.date) < new Date();
    }).length,
  }), [searchFiltered]);

  const statCards = useMemo(() => {
    const cards = tasksOnly
      ? [
        { key: 'total', label: p('stat_total'), value: stats.total, icon: PageIcon },
        { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
        { key: 'overdue', label: p('stat_overdue'), value: stats.overdue, icon: AlertCircle },
        { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle, variant: 'forest' },
      ]
      : [
        { key: 'total', label: p('stat_total'), value: stats.total, icon: PageIcon },
        { key: 'scheduled', label: p('stat_scheduled'), value: stats.scheduled, icon: Calendar, variant: 'forest' },
        { key: 'completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle },
        { key: 'pending', label: p('stat_pending') || ps('status_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      ];
    return sliceQawaemStats(cards, 4);
  }, [tasksOnly, stats, p, ps, PageIcon]);

  const { dataColumns, statusColumn } = useMemo(
    () => partitionStatusFromColumns(tableColumnDefs),
    [tableColumnDefs],
  );

  const timelineStatusOptions = useMemo(() => enrichStatusOptions([
    { value: 'completed', label: ps('status_completed'), icon: CheckCircle },
    { value: 'scheduled', label: ps('status_scheduled'), icon: Clock },
    { value: 'pending', label: ps('status_pending'), icon: AlertCircle },
    { value: 'cancelled', label: ps('status_cancelled'), icon: XCircle },
  ]), [ps]);

  const timelineStatusFilterOptions = useMemo(
    () => timelineStatusOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    [timelineStatusOptions],
  );

  const typeFilterOptions = useMemo(() => [
    { value: 'call', label: ps('type_call') },
    { value: 'email', label: ps('type_email') },
    { value: 'meeting', label: ps('type_meeting') },
    { value: 'whatsapp', label: ps('type_whatsapp') },
    { value: 'task', label: ps('type_task') },
    { value: 'note', label: ps('type_note') },
  ], [ps]);

  const priorityFilterOptions = useMemo(() => [
    { value: 'high', label: ps('priority_high') },
    { value: 'medium', label: ps('priority_medium') },
    { value: 'low', label: ps('priority_low') },
  ], [ps]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/crm/timeline/${recordId}`, { status: newStatus });
      dispatchCrmApiRefresh('/crm/timeline');
      loadRecords();
    } catch {
      alert(ps('save_error') || t('common.save_error', { defaultValue: 'تعذر حفظ التغيير' }));
    }
  };

  const qawaemColumnDefs = useMemo(() => {
    const defs = dataColumns.map((col) => ({
      key: col.key,
      label: p(col.labelKey) || col.labelKey,
      checked: columnVisibility[col.key] !== false,
    }));
    if (statusColumn) {
      defs.push({
        key: statusColumn.key,
        label: p(statusColumn.labelKey) || statusColumn.labelKey,
        checked: columnVisibility[statusColumn.key] !== false,
      });
    }
    return defs;
  }, [dataColumns, statusColumn, columnVisibility, p]);

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const visibleDataColumns = useMemo(
    () => dataColumns.filter((c) => columnVisibility[c.key] !== false),
    [dataColumns, columnVisibility],
  );

  const showStatusColumn = statusColumn && columnVisibility[statusColumn.key] !== false;

  const renderCellValue = (r, col) => {
    switch (col.key) {
      case 'ref': return r.ref;
      case 'title': return notesOnly ? r.notes : r.title;
      case 'customer': return r.customer;
      case 'type': return typeLabel(r.type);
      case 'date': return formatCrmDate(r.date, i18n.language, { includeTime: !notesOnly });
      case 'priority': return priorityLabel(r.priority);
      case 'status': return statusLabel(r.status);
      case 'dealAmount': return r.dealAmount != null ? formatCrmMoney(r.dealAmount) : '—';
      case 'assignedTo': return r.assignedTo;
      default: return '—';
    }
  };

  const renderCell = (r, col) => {
    if (col.key === 'date') {
      return (
        <>
          {formatCrmDate(r.date, i18n.language, { includeTime: !notesOnly })}
          {isSaudiWeekend(r.date) && <span className="block text-amber-400 text-xs">{p('weekend_warning')}</span>}
        </>
      );
    }
    if (col.key === 'title' && !notesOnly) {
      return <span className="max-w-xs truncate block">{r.title}</span>;
    }
    return renderCellValue(r, col);
  };

  const getColumnFilterKind = (colKey) => {
    if (colKey === 'type' || colKey === 'priority' || colKey === 'status') return 'select';
    return 'text';
  };

  const getColumnFilterOptions = (colKey) => {
    if (colKey === 'type') return typeFilterOptions;
    if (colKey === 'priority') return priorityFilterOptions;
    if (colKey === 'status') return timelineStatusFilterOptions;
    return undefined;
  };

  const columnFilterRules = useMemo(() => {
    const cols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) cols.push(statusColumn);
    return cols.map((col) => {
      const getValue = (row) => String(renderCellValue(row, col) ?? '');
      if (getColumnFilterKind(col.key) === 'select') {
        return exactColumnRule(col.key, getValue);
      }
      return textColumnRule(col.key, getValue);
    });
  }, [visibleDataColumns, showStatusColumn, statusColumn, notesOnly, i18n.language]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const tableColSpan = visibleDataColumns.length + (showStatusColumn ? 1 : 0) + 2;

  const handleExportCsv = () => {
    const exportCols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) exportCols.push(statusColumn);
    const headers = exportCols.map((col) => ({ key: col.key, label: p(col.labelKey) || col.labelKey }));
    const rows = filtered.map((r) => {
      const row = {};
      exportCols.forEach((col) => { row[col.key] = renderCellValue(r, col); });
      return row;
    });
    exportToCSV(rows, headers, `${pageKey}-${new Date().toISOString().split('T')[0]}.csv`);
    setExportOpen(false);
  };

  return (
    <>
      <QawaemErpListPageShell
        title={p('title')}
        subtitle={p('subtitle')}
        icon={PageIcon}
        stats={statCards}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        columns={qawaemColumnDefs}
        visibleColumnCount={visibleDataColumns.length + (showStatusColumn ? 1 : 0)}
        totalColumnCount={dataColumns.length + (statusColumn ? 1 : 0)}
        headerActions={(
          <button type="button" onClick={loadRecords} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
        importExport={{
          importOpen: false,
          exportOpen,
          onToggleImport: () => {},
          onToggleExport: () => setExportOpen((v) => !v),
          importRef: exportRef,
          exportRef,
          importLabel: p('import_label') || t('common.import', { defaultValue: 'استيراد' }),
          exportLabel: p('export_label') || t('common.export', { defaultValue: 'تصدير' }),
          generatingLabel: t('common.loading'),
          isGeneratingPDF: false,
          onImportCSV: () => {},
          onImportExcel: () => {},
          onExportPDF: () => {},
          onExportExcel: () => {},
          onExportCSV: handleExportCsv,
        }}
      >
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {visibleDataColumns.map((col) => (
                    <TableColumnHeaderFilter
                      key={col.key}
                      columnKey={col.key}
                      label={p(col.labelKey) || col.labelKey}
                      kind={getColumnFilterKind(col.key)}
                      value={colFilters[col.key] ?? ''}
                      onChange={(v) => setColFilter(col.key, v)}
                      options={getColumnFilterOptions(col.key)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                      className="px-6 py-4"
                    />
                  ))}
                  {showStatusColumn ? (
                    <TableColumnHeaderFilter
                      columnKey={statusColumn.key}
                      label={p(statusColumn.labelKey) || statusColumn.labelKey}
                      kind="select"
                      value={colFilters[statusColumn.key] ?? ''}
                      onChange={(v) => setColFilter(statusColumn.key, v)}
                      options={timelineStatusFilterOptions}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="p-12 text-center text-white/60">{ps('loading')}</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="p-12 text-center text-white/60">{p('empty_state')}</td>
                  </tr>
                ) : pagedFiltered.map((r) => (
                  <tr key={r.id} className={`hover:bg-white/5 text-white/90 text-sm ${selectedRecords.includes(r.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(r.id)}
                      onChange={() => handleSelectRecord(r.id)}
                    />
                    {visibleDataColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-6 py-4 ${col.key === 'ref' ? 'font-mono text-xs' : ''} ${col.key === 'dealAmount' ? 'font-semibold text-emerald-400' : ''}`}
                      >
                        {renderCell(r, col)}
                      </td>
                    ))}
                    {showStatusColumn ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={r.status}
                            recordId={r.id}
                            options={timelineStatusOptions}
                            onChange={handleStatusChange}
                            title={ps('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => { setViewingRecord(r); setShowViewModal(true); }}
                        viewTitle={ps('view_details') || t('common.view')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="p-12 text-center text-white/60">{ps('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-white/60">{p('empty_state')}</div>
        ) : (
          <div className="p-6 grid gap-4 md:grid-cols-3">
            {pagedFiltered.map((r) => (
              <div key={r.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 h-full min-w-0">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-semibold text-white">{notesOnly ? r.notes?.slice(0, 60) : r.title}</span>
                  <span className="text-xs text-white/50">{typeLabel(r.type)}</span>
                </div>
                <p className="text-white/60 text-sm mb-2">{r.customer}</p>
                <p className="text-white/50 text-xs">{formatCrmDate(r.date, i18n.language, { includeTime: !notesOnly })}</p>
                <div className="mt-2">
                  <QawaemClickableStatusBadge
                    status={r.status}
                    recordId={r.id}
                    options={timelineStatusOptions}
                    onChange={handleStatusChange}
                  />
                </div>
                {r.dealAmount != null && <p className="text-emerald-400 font-bold mt-2">{formatCrmMoney(r.dealAmount)}</p>}
                <QawaemErpRowActions
                  compact
                  size={14}
                  className="mt-3 justify-start"
                  onView={() => { setViewingRecord(r); setShowViewModal(true); }}
                  viewTitle={ps('view_details') || t('common.view')}
                />
              </div>
            ))}
          </div>
        )}
      </QawaemErpListPageShell>

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={ps('view_details')}
          icon={Activity}
          maxWidth="640px"
          testId="crm-timeline-view-modal"
        >
          <div className={`space-y-3 text-sm ${cfgTheme.light ? 'text-slate-700' : 'text-white/80'}`} dir="rtl">
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_ref')}:</span> {viewingRecord.ref}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_title')}:</span> {viewingRecord.title}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_customer')}:</span> {viewingRecord.customer}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_date')}:</span> {formatCrmDate(viewingRecord.date, i18n.language, { includeTime: true })}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_type')}:</span> {typeLabel(viewingRecord.type)}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_priority')}:</span> {priorityLabel(viewingRecord.priority)}</p>
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_status')}:</span> {statusLabel(viewingRecord.status)}</p>
            {viewingRecord.dealAmount != null && <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_deal_amount')}:</span> <strong className="text-emerald-400">{formatCrmMoney(viewingRecord.dealAmount)}</strong></p>}
            {viewingRecord.notes && <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_notes')}:</span> {viewingRecord.notes}</p>}
            <p><span className={cfgTheme.light ? 'text-slate-500' : 'text-white/50'}>{ps('view_assigned')}:</span> {viewingRecord.assignedTo}</p>
          </div>
        </QawaemErpViewModal>
      ) : null}
    </>
  );
}
